/**
 * De que tamanho cada combatente entra no palco do duelo.
 *
 * O palco tinha uma altura só para todo inimigo — `min(360px, 46vh)` —, e uma
 * altura só apaga justamente a informação que o desenho carrega. Os battlers do
 * acervo vão de 66×84 (um ovo com pernas) a 552×320 (um deus demoníaco): forçar
 * os dois a 360 de altura amplia o ovo em quatro vezes e ENCOLHE o deus, e a
 * cena passa a mostrar um ovo do tamanho de um dragão ao lado de um herói que
 * lhe bate no joelho. Era o defeito relatado — "monstros pequenos aparecem
 * grandes".
 *
 * A saída é a mesma do tabuleiro: comprimir a diferença em vez de anulá-la. Um
 * battler quatro vezes maior aparece cerca de duas vezes maior, o herói fica no
 * meio da escala, e nenhum dos dois extremos estoura o palco.
 *
 * Tudo aqui é aritmética pura, medida contra a altura de projeto do palco;
 * quem desenha converte para as unidades do contêiner e a cena inteira encolhe
 * junto em telas menores.
 */

/** A altura para a qual o palco foi desenhado. Ver `.combat-scene-container`. */
export const STAGE_HEIGHT = 660;

/** O battler de porte médio do acervo, contra o qual os outros são medidos. */
const STAGE_REFERENCE = 180;
/** E a altura com que ele entra — a mesma do herói, por construção. */
const STAGE_BASE = 250;
/**
 * O quanto a diferença de tamanho sobrevive à compressão.
 *
 * Em 1 o deus demoníaco entraria com seis vezes a altura do ovo e sairia pelo
 * teto do palco; em 0 todos entram iguais, que é o defeito que se está
 * consertando. Em 0,6 a ordem se mantém e a diferença se lê.
 */
const STAGE_COMPRESSION = 0.6;
/** Nem o menor bicho do acervo vira um ícone... */
const STAGE_MIN = 120;
/** ...nem o maior cobre o palco inteiro. */
const STAGE_MAX = 430;

/**
 * O quadro de um battler de `sv_actors`.
 *
 * A folha guarda poses de 64×64 e o palco as amplia; 3,91 é a ampliação que a
 * cena já usava, e é ela que fixa os 250 de referência acima.
 */
export const ACTOR_FRAME_SCALE = 3.91;

export interface StageBox {
  readonly width: number;
  readonly height: number;
}

/** O quadro de um herói: quadrado, do tamanho do quadro da folha ampliado. */
export function actorStageBox(frameSize: number): StageBox {
  const side = frameSize * ACTOR_FRAME_SCALE;
  return { width: side, height: side };
}

/** O quadro de um monstro: a arte inteira, comprimida contra a referência. */
export function enemyStageBox(width: number, height: number): StageBox {
  // As duas medidas precisam ser boas: o manifesto de um asset truncado pode
  // trazer zero, e uma delas negativa devolveria um quadro de largura negativa,
  // que o navegador simplesmente ignora — o battler sumiria da cena.
  const usable =
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
  if (!usable) return { width: STAGE_BASE, height: STAGE_BASE };

  const natural = Math.max(width, height);

  const wanted = STAGE_BASE * (natural / STAGE_REFERENCE) ** STAGE_COMPRESSION;
  const longest = Math.min(STAGE_MAX, Math.max(STAGE_MIN, wanted));

  // O maior lado recebe o tamanho escolhido e o outro segue a proporção: a arte
  // é contida no quadro, nunca esticada.
  return { width: (longest * width) / natural, height: (longest * height) / natural };
}

/**
 * O mesmo comprimento, na medida do palco.
 *
 * `cqh` é um centésimo da altura do contêiner do combate. Escrever assim, e não
 * em pixels, é o que faz a cena inteira encolher proporcionalmente numa janela
 * pequena em vez de cortar o battler pela metade.
 */
export function stageUnits(px: number): string {
  return `${((px / STAGE_HEIGHT) * 100).toFixed(3)}cqh`;
}
